import React from "react";
import PropTypes from 'prop-types';
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import MarkEmailUnreadOutlined from "@mui/icons-material/MarkEmailUnreadOutlined";
import UnarchiveOutlined from "@mui/icons-material/UnarchiveOutlined";

const PREVIEW_LEN = 120;

function previewText(text) {
  if (!text || typeof text !== "string") return "";
  const t = text.trim();
  if (t.length <= PREVIEW_LEN) return t;
  return `${t.slice(0, PREVIEW_LEN).trimEnd()}...`;
}

function NotifTooltip({ title, children }) {
  return (
    <Tooltip title={title} placement="top" arrow copyable={false}>
      <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

NotifTooltip.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
};

// Regex per evidenziare le parole "errore/errori" nel testo
const SPLIT_ERROR_RE = /(errore|errori)/gi;
const ERROR_WORD_RE = /^errore|errori$/i;

function colorErrorWords(text, normalColor, errorColor) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(SPLIT_ERROR_RE);

  // Chiave stabile basata sull'offset del frammento nel testo
  let offset = 0;
  return parts.map((part) => {
    const key = `${offset}-${part}`;
    offset += part.length;
    const color = ERROR_WORD_RE.exec(part) ? errorColor : normalColor;
    return (
      <span key={key} style={{ color }}>
        {part}
      </span>
    );
  });
}

const iconBtnSx = { p: 0.75 };

export default function CardNotifiche({
  notification,
  typeLabel,
  variant,
  onOpen,
  onToggleRead,
  onArchive,
  onDelete,
  onRestore,
  readOnly = false,
}) {
  const isArchived = variant === "archived";
  const unread = !notification.isRead && !isArchived;

  return (
    <Paper
      elevation={0}
      onClick={() => onOpen(notification)}
      sx={{
        p: 2,
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        borderLeft: unread ? "4px solid #0b3d87" : "1px solid #e2e8f0",
        backgroundColor: unread ? "#f7f9fc" : "#fff",
        cursor: "pointer",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: 2,
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
            <Typography
              sx={{
                fontFamily: "inherit",
                fontSize: 12,
                color: "#808080",
              }}
            >
              {notification.date}
            </Typography>
            {typeLabel ? (
              <Chip
                label={typeLabel}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  borderColor: "#0b3d87",
                  color: "#0b3d87",
                }}
              />
            ) : null}
          </Box>
          <Typography
            sx={{
              fontFamily: "inherit",
              fontWeight: unread ? "bold" : "normal",
              fontSize: 16,
              mb: 1,
              wordBreak: "break-word",
            }}
          >
            {colorErrorWords(notification.title, "#0b3d87", "#e00036")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: unread ? "bold" : 400,
              lineHeight: 1.45,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {colorErrorWords(previewText(notification.description), "#4a4a4a", "#e00036")}
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          flexShrink={0}
          onClick={(e) => e.stopPropagation()}
        >
          {isArchived ? (
            <>
              <NotifTooltip title="Ripristina la notifica nell'elenco principale">
                <IconButton
                  size="small"
                  aria-label="Ripristina notifica"
                  sx={iconBtnSx}
                  disabled={readOnly}
                  onClick={() => onRestore(notification.id)}
                >
                  <UnarchiveOutlined sx={{ color: "#0b3d87" }} />
                </IconButton>
              </NotifTooltip>
              <NotifTooltip title="Elimina la notifica">
                <IconButton
                  size="small"
                  aria-label="Elimina notifica"
                  sx={iconBtnSx}
                  disabled={readOnly}
                  onClick={() => onDelete(notification.id)}
                >
                  <DeleteOutline sx={{ color: "#e00036" }} />
                </IconButton>
              </NotifTooltip>
            </>
          ) : (
            <>
              <NotifTooltip title={notification.isRead ? "Segna come non letta" : "Segna come letta"}>
                <IconButton
                  size="small"
                  aria-label={notification.isRead ? "Segna come non letta" : "Segna come letta"}
                  sx={iconBtnSx}
                  disabled={readOnly}
                  onClick={() => onToggleRead(notification.id)}
                >
                  {notification.isRead ? (
                    <MarkEmailUnreadOutlined sx={{ color: "#0b3d87" }} />
                  ) : (
                    <MarkEmailReadOutlined sx={{ color: "#0b3d87" }} />
                  )}
                </IconButton>
              </NotifTooltip>
              <NotifTooltip title="Archivia la notifica">
                <IconButton
                  size="small"
                  aria-label="Archivia notifica"
                  sx={iconBtnSx}
                  disabled={readOnly}
                  onClick={() => onArchive(notification.id)}
                >
                  <ArchiveOutlined sx={{ color: "#ed6c02" }} />
                </IconButton>
              </NotifTooltip>
              <NotifTooltip title="Elimina la notifica">
                <IconButton
                  size="small"
                  aria-label="Elimina notifica"
                  sx={iconBtnSx}
                  disabled={readOnly}
                  onClick={() => onDelete(notification.id)}
                >
                  <DeleteOutline sx={{ color: "#e00036" }} />
                </IconButton>
              </NotifTooltip>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

CardNotifiche.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    date: PropTypes.string,
    isRead: PropTypes.bool,
  }).isRequired,
  typeLabel: PropTypes.string,
  variant: PropTypes.string,
  onOpen: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
 